import { Node, Beam } from '@/types/nnstructural2D';
import { isNodeDuplicate, isBeamDuplicate } from './duplicateUtils';

export interface DuplicateGroup<T> {
  id: number;
  items: Array<{
    item: T;
    source: 'existing' | 'imported';
    originalIndex?: number;
  }>;
  duplicateKey: string; // For nodes: "x,y,z", for beams: "startId-endId"
}

export interface DuplicateGroupResult<T> {
  groups: DuplicateGroup<T>[];
  nonDuplicateItems: T[];
}

/**
 * Generate a key for grouping duplicate nodes
 */
const getNodeDuplicateKey = (node: Node): string => {
  return `${node.x.toFixed(6)},${node.y.toFixed(6)},${node.z.toFixed(6)}`;
};

/**
 * Generate a key for grouping duplicate beams
 */
const getBeamDuplicateKey = (beam: Beam): string => {
  // Sort the node IDs to handle bidirectional duplicates
  const [start, end] = [beam.startNodeId, beam.endNodeId].sort();
  return `${start}-${end}`;
};

/**
 * Find node duplicate groups
 */
export function findNodeDuplicateGroups(
  existingNodes: Node[],
  importedNodes: Node[]
): DuplicateGroupResult<Node> {
  const groups: DuplicateGroup<Node>[] = [];
  const nonDuplicateItems: Node[] = [];
  const processedKeys = new Set<string>();
  
  // Create a map of all nodes by their duplicate key
  const allNodes = [
    ...existingNodes.map(node => ({ 
      item: node, 
      source: 'existing' as const, 
      key: getNodeDuplicateKey(node) 
    })),
    ...importedNodes.map((node, index) => ({ 
      item: node, 
      source: 'imported' as const, 
      originalIndex: index,
      key: getNodeDuplicateKey(node) 
    }))
  ];
  
  // Group nodes by their duplicate key
  const nodesByKey = new Map<string, Array<{
    item: Node;
    source: 'existing' | 'imported';
    originalIndex?: number;
  }>>();
  
  allNodes.forEach(nodeInfo => {
    const key = nodeInfo.key;
    if (!nodesByKey.has(key)) {
      nodesByKey.set(key, []);
    }
    nodesByKey.get(key)!.push({
      item: nodeInfo.item,
      source: nodeInfo.source,
      originalIndex: nodeInfo.originalIndex
    });
  });
  
  let groupId = 1;
  
  // Process each unique coordinate combination
  nodesByKey.forEach((nodeGroup, key) => {
    console.log(`🔑 Processing coordinate key: ${key} with ${nodeGroup.length} items`);
    nodeGroup.forEach(item => {
      console.log(`  - ${item.source} node ID: ${item.item.id}`);
    });
    
    if (nodeGroup.length > 1) {
      // This is a duplicate group
      console.log(`✋ Creating duplicate group for coordinate: ${key}`);
      groups.push({
        id: groupId++,
        items: nodeGroup,
        duplicateKey: key
      });
    } else {
      // Single item - check if it's imported (non-duplicate)
      const singleNode = nodeGroup[0];
      if (singleNode.source === 'imported') {
        console.log(`✅ Adding non-duplicate imported node: ${singleNode.item.id} at ${key}`);
        nonDuplicateItems.push(singleNode.item);
      }
    }
  });
  
  return { groups, nonDuplicateItems };
}

/**
 * Find beam duplicate groups
 */
export function findBeamDuplicateGroups(
  existingBeams: Beam[],
  importedBeams: Beam[]
): DuplicateGroupResult<Beam> {
  const groups: DuplicateGroup<Beam>[] = [];
  const nonDuplicateItems: Beam[] = [];
  
  // Create a map of all beams by their duplicate key
  const allBeams = [
    ...existingBeams.map(beam => ({ 
      item: beam, 
      source: 'existing' as const, 
      key: getBeamDuplicateKey(beam) 
    })),
    ...importedBeams.map((beam, index) => ({ 
      item: beam, 
      source: 'imported' as const, 
      originalIndex: index,
      key: getBeamDuplicateKey(beam) 
    }))
  ];
  
  // Group beams by their duplicate key
  const beamsByKey = new Map<string, Array<{
    item: Beam;
    source: 'existing' | 'imported';
    originalIndex?: number;
  }>>();
  
  allBeams.forEach(beamInfo => {
    const key = beamInfo.key;
    if (!beamsByKey.has(key)) {
      beamsByKey.set(key, []);
    }
    beamsByKey.get(key)!.push({
      item: beamInfo.item,
      source: beamInfo.source,
      originalIndex: beamInfo.originalIndex
    });
  });
  
  let groupId = 1;
  
  // Process each unique node pair combination
  beamsByKey.forEach((beamGroup, key) => {
    if (beamGroup.length > 1) {
      // This is a duplicate group
      groups.push({
        id: groupId++,
        items: beamGroup,
        duplicateKey: key
      });
    } else {
      // Single item - check if it's imported (non-duplicate)
      const singleBeam = beamGroup[0];
      if (singleBeam.source === 'imported') {
        nonDuplicateItems.push(singleBeam.item);
      }
    }
  });
  
  return { groups, nonDuplicateItems };
}

/**
 * Resolve duplicate groups based on user choices
 */
export function resolveDuplicateGroups<T>(
  groups: DuplicateGroup<T>[],
  nonDuplicateItems: T[],
  resolutions: Record<number, Array<{ source: 'existing' | 'imported'; index: number }>>
): {
  finalItems: T[];
  removedCount: number;
} {
  const finalItems: T[] = [...nonDuplicateItems];
  let removedCount = 0;
  
  groups.forEach(group => {
    const resolution = resolutions[group.id];
    if (resolution && resolution.length > 0) {
      // Add only the selected items from this group
      resolution.forEach(choice => {
        const selectedItem = group.items.find(
          (item, index) => item.source === choice.source && 
          (choice.source === 'existing' || item.originalIndex === choice.index)
        );
        if (selectedItem) {
          finalItems.push(selectedItem.item);
        }
      });
      removedCount += group.items.length - resolution.length;
    } else {
      // No resolution provided - keep all items (should not happen in normal flow)
      group.items.forEach(item => {
        finalItems.push(item.item);
      });
    }
  });
  
  return { finalItems, removedCount };
}

/**
 * Get items to import in append mode (excludes all duplicates)
 * This returns only imported items that should be added in append mode:
 * - No duplicates against existing items
 * - Only first occurrence of duplicate coordinates within imported data
 */
export function getItemsForAppend<T extends Node | Beam>(
  duplicateGroupResult: DuplicateGroupResult<T>
): T[] {
  const itemsToImport: T[] = [...duplicateGroupResult.nonDuplicateItems];
  
  // For each duplicate group, only add the first imported item (if any)
  // and only if there are no existing items in the group
  duplicateGroupResult.groups.forEach(group => {
    const hasExisting = group.items.some(item => item.source === 'existing');
    
    if (!hasExisting) {
      // This group contains only imported duplicates - take the first one
      const firstImported = group.items.find(item => item.source === 'imported');
      if (firstImported) {
        itemsToImport.push(firstImported.item);
      }
    }
    // If hasExisting = true, we don't import any items from this group
  });
  
  return itemsToImport;
}

/**
 * Get summary statistics for duplicate groups
 */
export function getDuplicateGroupStats<T>(
  result: DuplicateGroupResult<T>
): {
  totalGroups: number;
  totalDuplicateItems: number;
  totalNonDuplicateItems: number;
  affectedItems: number;
} {
  const totalGroups = result.groups.length;
  const totalDuplicateItems = result.groups.reduce((sum, group) => sum + group.items.length, 0);
  const totalNonDuplicateItems = result.nonDuplicateItems.length;
  const affectedItems = totalDuplicateItems;
  
  return {
    totalGroups,
    totalDuplicateItems,
    totalNonDuplicateItems,
    affectedItems
  };
}
